/**
 * Base API configuration
 */

import { API_BASE_URL } from './apiConfig';
import { ensureFreshToken } from './tokenManager';

const API_LOGGING_ENABLED = import.meta.env.VITE_REACT_API_LOGGING === 'true';

/**
 * Base fetch wrapper with error handling
 */
export async function apiFetch(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;

  const { skipAuth, ...fetchOptions } = options;
  const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;

  const config = {
    headers: isFormData
      ? { ...fetchOptions.headers } // let the browser set the multipart boundary
      : { 'Content-Type': 'application/json', ...fetchOptions.headers },
    ...fetchOptions,
  };

  // Add auth token if available (skip for public endpoints)
  if (!skipAuth) {
    const token = await ensureFreshToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
  }

  if (API_LOGGING_ENABLED) {
    console.log(`[API Request] ${options.method || 'GET'} ${url}`, {
      headers: config.headers,
      body: options.body,
    });
  }

  try {
    const response = await fetch(url, config);
    
    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');
    
    const data = isJson ? await response.json() : await response.text();
    
    if (API_LOGGING_ENABLED) {
      console.log(`[API Response] ${response.status} ${url}`, data);
    }
    
    if (!response.ok) {
      // Handle 401 Unauthorized - clear token and redirect to login
      if (response.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('userData');
        localStorage.removeItem('userRole');
        window.location.href = '/';
      }
      
      throw new Error(data.message || data.error || `HTTP Error ${response.status}`);
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', error);
    throw error;
  }
}

/**
 * API methods
 */
export const api = {
  // GET request
  get: (endpoint, options) => 
    apiFetch(endpoint, { ...options, method: 'GET' }),
  
  // POST request
  post: (endpoint, body, options) => 
    apiFetch(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body),
    }),
  
  // PUT request
  put: (endpoint, body, options) => 
    apiFetch(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  
  // PATCH request
  patch: (endpoint, body, options) => 
    apiFetch(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body),
    }),
  
  // DELETE request
  delete: (endpoint, options) => 
    apiFetch(endpoint, { ...options, method: 'DELETE' }),
};

export { API_BASE_URL };

export default api;